import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { toast } from 'sonner';
import { Plus, Trash2, ChevronDown, ChevronRight, Package, X } from 'lucide-react';

interface BundleItem {
  product_id: string;
  product_name?: string;
  quantity: number;
}

interface Bundle {
  id: string;
  bundle_name: string;
  unit_of_measure: string;
  is_active: boolean;
  created_at: string;
  product_bundle_items: Array<{
    id: string;
    product_id: string;
    quantity: number;
    products?: { name: string };
  }>;
}

export function ProductBundlesTab() {
  const queryClient = useQueryClient();
  const [openBundles, setOpenBundles] = useState<Set<string>>(new Set());
  const [dialogOpen, setDialogOpen] = useState(false);
  const [bundleName, setBundleName] = useState('');
  const [unitOfMeasure, setUnitOfMeasure] = useState('kg');
  const [bundleItems, setBundleItems] = useState<BundleItem[]>([]);
  const [selectedProductId, setSelectedProductId] = useState('');
  const [itemQuantity, setItemQuantity] = useState('1');

  const { data: bundles, isLoading } = useQuery({
    queryKey: ['product-bundles'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('product_bundles')
        .select('id, bundle_name, unit_of_measure, is_active, created_at, product_bundle_items(id, product_id, quantity, products:product_id(name))')
        .order('bundle_name');
      if (error) throw error;
      return data as unknown as Bundle[];
    },
  });

  const { data: products } = useQuery({
    queryKey: ['products-for-bundles'],
    queryFn: async () => {
      const { data, error } = await supabase.from('products').select('id, name').order('name');
      if (error) throw error;
      return data;
    },
  });

  const createBundleMutation = useMutation({
    mutationFn: async () => {
      const { data: bundle, error } = await supabase
        .from('product_bundles')
        .insert({ bundle_name: bundleName, unit_of_measure: unitOfMeasure })
        .select('id')
        .single();
      if (error) throw error;

      if (bundleItems.length > 0) {
        const { error: itemsError } = await supabase
          .from('product_bundle_items')
          .insert(bundleItems.map(item => ({
            bundle_id: bundle.id,
            product_id: item.product_id,
            quantity: item.quantity,
          })));
        if (itemsError) throw itemsError;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['product-bundles'] });
      toast.success('Bundle created');
      resetForm();
    },
    onError: (err: any) => toast.error(err.message),
  });

  const deleteBundleMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('product_bundles').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['product-bundles'] });
      toast.success('Bundle deleted');
    },
    onError: (err: any) => toast.error(err.message),
  });

  const toggleActive = useMutation({
    mutationFn: async ({ id, is_active }: { id: string; is_active: boolean }) => {
      const { error } = await supabase.from('product_bundles').update({ is_active }).eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['product-bundles'] });
    },
  });

  const resetForm = () => {
    setBundleName('');
    setUnitOfMeasure('kg');
    setBundleItems([]);
    setSelectedProductId('');
    setItemQuantity('1');
    setDialogOpen(false);
  };

  const addItem = () => {
    if (!selectedProductId || !itemQuantity) return;
    const product = products?.find(p => p.id === selectedProductId);
    if (bundleItems.some(i => i.product_id === selectedProductId)) {
      toast.warning('Product already in bundle');
      return;
    }
    setBundleItems(prev => [...prev, {
      product_id: selectedProductId,
      product_name: product?.name,
      quantity: parseFloat(itemQuantity),
    }]);
    setSelectedProductId('');
    setItemQuantity('1');
  };

  const removeItem = (productId: string) => {
    setBundleItems(prev => prev.filter(i => i.product_id !== productId));
  };

  const toggleBundle = (id: string) => {
    setOpenBundles(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="flex items-center gap-2">
          <Package className="h-5 w-5" />
          Product Bundles
        </CardTitle>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button size="sm"><Plus className="h-4 w-4 mr-1" /> Add Bundle</Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Create Bundle</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium">Bundle Name</label>
                <Input value={bundleName} onChange={e => setBundleName(e.target.value)} placeholder="e.g. fruit package bundle" />
              </div>
              <div>
                <label className="text-sm font-medium">Unit</label>
                <Select value={unitOfMeasure} onValueChange={setUnitOfMeasure}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="kg">KG</SelectItem>
                    <SelectItem value="pcs">Pieces</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium">Component Products</label>
                <div className="flex gap-2">
                  <Select value={selectedProductId} onValueChange={setSelectedProductId}>
                    <SelectTrigger className="flex-1"><SelectValue placeholder="Select product" /></SelectTrigger>
                    <SelectContent>
                      {products?.map(p => (
                        <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Input
                    type="number"
                    className="w-20"
                    value={itemQuantity}
                    onChange={e => setItemQuantity(e.target.value)}
                    min="0.1"
                    step="0.1"
                  />
                  <Button size="sm" onClick={addItem} variant="outline"><Plus className="h-4 w-4" /></Button>
                </div>
                {bundleItems.length > 0 && (
                  <div className="border rounded-md p-2 space-y-1">
                    {bundleItems.map(item => (
                      <div key={item.product_id} className="flex items-center justify-between text-sm">
                        <span>{item.product_name}</span>
                        <div className="flex items-center gap-2">
                          <Badge variant="secondary">{item.quantity} {unitOfMeasure}</Badge>
                          <Button size="icon" variant="ghost" className="h-6 w-6" onClick={() => removeItem(item.product_id)}>
                            <X className="h-3 w-3" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <Button
                onClick={() => createBundleMutation.mutate()}
                disabled={!bundleName || bundleItems.length === 0 || createBundleMutation.isPending}
                className="w-full"
              >
                Create Bundle
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <p className="text-sm text-muted-foreground">Loading...</p>
        ) : !bundles?.length ? (
          <p className="text-sm text-muted-foreground">No bundles created yet.</p>
        ) : (
          <div className="space-y-2">
            {bundles.map(bundle => (
              <Collapsible key={bundle.id} open={openBundles.has(bundle.id)}>
                <div className="border rounded-lg">
                  <div className="flex items-center justify-between px-4 py-3">
                    <CollapsibleTrigger asChild>
                      <button className="flex items-center gap-2 text-left flex-1" onClick={() => toggleBundle(bundle.id)}>
                        {openBundles.has(bundle.id) ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                        <span className="font-medium">{bundle.bundle_name}</span>
                        <Badge variant="outline" className="text-xs">{bundle.unit_of_measure.toUpperCase()}</Badge>
                        <Badge variant="secondary" className="text-xs">{bundle.product_bundle_items.length} items</Badge>
                        {!bundle.is_active && <Badge variant="destructive" className="text-xs">Inactive</Badge>}
                      </button>
                    </CollapsibleTrigger>
                    <div className="flex items-center gap-1">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => toggleActive.mutate({ id: bundle.id, is_active: !bundle.is_active })}
                      >
                        {bundle.is_active ? 'Disable' : 'Enable'}
                      </Button>
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-8 w-8 text-destructive"
                        onClick={() => {
                          if (confirm('Delete this bundle?')) deleteBundleMutation.mutate(bundle.id);
                        }}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                  <CollapsibleContent>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Product</TableHead>
                          <TableHead className="text-right">Quantity</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {bundle.product_bundle_items.map(item => (
                          <TableRow key={item.id}>
                            <TableCell>{(item.products as any)?.name || 'Unknown'}</TableCell>
                            <TableCell className="text-right">{item.quantity} {bundle.unit_of_measure}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </CollapsibleContent>
                </div>
              </Collapsible>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
